import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";

import { APIError } from "@btnet/services-btnet/errors";

import { MediaTypes } from "handlers";

export enum FileSizeUnit {
    Byte = "B",
    Kilobyte = "KB",
    Megabyte = "MB",
    Gigabyte = "GB",
    Terabyte = "TB",
}

export function convertFileSizeToBytes(size: number, unit: FileSizeUnit) {
    switch (unit) {
        case FileSizeUnit.Byte:
            return size;
        case FileSizeUnit.Kilobyte:
            return size * 1024;
        case FileSizeUnit.Megabyte:
            return size * 1024 ** 2;
        case FileSizeUnit.Gigabyte:
            return size * 1024 ** 3;
        case FileSizeUnit.Terabyte:
            return size * 1024 ** 4;
    }
}

/**
 * @param fileName a file name with the last "." and extension following it
 * @returns
 * the extension of the file (without the period)
 * @example
 * getFileExtension("myFile.png")
 */
export function getFileExtension(fileName: string) {
    return fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);
}

/**
 * True when two file extensions refer to the same format (e.g. "jpg"/"jpeg",
 * "tif"/"tiff"). Case- and leading-dot-insensitive. Use this to decide whether the
 * existing extension on a filename already matches the format and can be left alone.
 */
export function areEquivalentExtensions(a: string, b: string): boolean {
    const canonical = (ext: string) => {
        const lower = ext.toLowerCase().replace(/^\./, "");
        if (lower === "jpeg") return "jpg";
        if (lower === "tiff") return "tif";
        return lower;
    };
    return canonical(a) === canonical(b);
}

/**
 * Maps a MIME / content-type string to a canonical file extension.
 * Returns `undefined` when no explicit mapping exists — callers should fall back to
 * whatever source extension they already have.
 */
export function getExtensionFromMimeType(mime: string): string | undefined {
    switch (mime.toLowerCase()) {
        case "image/jpeg":
        case "image/jpg":
            return "jpg";
        case "image/png":
            return "png";
        case "image/tiff":
            return "tif";
        case "image/bmp":
            return "bmp";
        case "image/gif":
            return "gif";
        case "image/svg+xml":
            return "svg";
        case "image/webp":
            return "webp";
        case "image/heic":
            return "heic";
        case "image/heif":
            return "heif";
        default:
            return undefined;
    }
}

/**
 *
 * @param file The file to validate
 * @param supportedFileExtensions An array of string of supported file types to allow. Each file type
 * should be prefixed with a "`.`"
 * @example
 * isSupportedFileExtension(myFile, [".png", ".jpg", ".jpeg", ".bmp"])
 */
export function isSupportedFileExtension(fileName: string, supportedFileExtensions: string[]) {
    // File name extension grabber that supports "hidden" files as well
    // https://stackoverflow.com/questions/190852/how-can-i-get-file-extensions-with-javascript/12900504#12900504
    const setOfSupportedExtensions = new Set(
        supportedFileExtensions.map((supportedFileExt) => {
            const isolatedSupportedExt = supportedFileExt.startsWith(".")
                ? supportedFileExt.substring(1)
                : supportedFileExt;
            return isolatedSupportedExt.toLocaleLowerCase();
        }),
    );

    if (setOfSupportedExtensions.has("*")) {
        return true;
    }

    const fileExtension = getFileExtension(fileName);
    return setOfSupportedExtensions.has(fileExtension.toLocaleLowerCase());
}

/**
 * Throws an exception if the given base64 encoded image string was not a valid/renderable image.
 */
export async function validateBase64Image(imageBase64: string, timeoutInMilliseconds = 10000) {
    const image = new Image();
    if (!image) {
        return Promise.reject(
            new Error("Could not attempt validation as an image object could not be created."),
        );
    }

    image.src = imageBase64;
    return new Promise<void>((resolve, reject) => {
        const timeoutHandle = setTimeout(() => {
            reject(
                new Error("Could not determine image validity because the operation timed out."),
            );
        }, timeoutInMilliseconds);

        image.onload = () => {
            clearTimeout(timeoutHandle);
            resolve();
        };

        image.onerror = () => {
            clearTimeout(timeoutHandle);
            reject(
                new Error(
                    "Image could not be rendered. Either the image is corrupt, its formating is not suppported by the current browser, or there was an issue with the base64 encoding.",
                ),
            );
        };
    });
}

export async function convertImageBlobToBase64String(blob: Blob, timeoutInMilliseconds = 10000) {
    return new Promise<string>((resolve, reject) => {
        const timeoutHandle = setTimeout(() => {
            reject(
                new Error(
                    "An error occurred while converting the image to a base64 encoding because the opperation timed out",
                ),
            );
        }, timeoutInMilliseconds);
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = function (event) {
            clearTimeout(timeoutHandle);
            if (event.target?.result && typeof event.target.result === "string") {
                try {
                    resolve(event.target.result);
                } catch (e) {
                    reject(
                        new Error(
                            "An error occurred while converting the image to a base64 encoding",
                        ),
                    );
                }
            } else {
                reject(
                    new Error(
                        "An error occurred while converting the image to a base64 encoding: " +
                            "The file reader did not return a valid result",
                    ),
                );
            }
        };
        reader.onerror = function (event) {
            clearTimeout(timeoutHandle);
            reject(event.target?.error ?? event);
        };
    });
}

/**
 * Loads a blob from a URL using the fetch() api
 * @param url Url of the blob to load
 * @param timeoutInMilliseconds Timeout in milliseconds for loading the blob
 */
export async function getBlobFromUrl(url: string, timeoutInMilliseconds = 15000) {
    const controller = new AbortController();
    const abortControllerTimeout = setTimeout(() => controller.abort(), timeoutInMilliseconds);

    let data;
    try {
        data = await fetch(url, { signal: controller.signal });
    } finally {
        clearTimeout(abortControllerTimeout);
    }

    return await data.blob();
}

/**
 * Loads an image from a URL using the fetch() api and then converts the image data to a base64 string
 * @param url Url of the image to load
 * @param timeoutInMilliseconds Timeout in milliseconds for loading the image and converting it to base64 encoding.
 * Note that the total time might be `2 * timeoutInMilliseconds` in the worst case as two operations
 * use the same timeout.
 * @returns
 */
export async function getBase64ImageFromUrl(url: string, timeoutInMilliseconds = 15000) {
    return await convertImageBlobToBase64String(
        await getBlobFromUrl(url, timeoutInMilliseconds),
        timeoutInMilliseconds,
    );
}

interface IGetLocalBlobUrlFromExternalResourceOptions {
    onProgressUpdate?: (progress: number) => void;
    timeoutInMilliseconds?: number;
    headers?: Record<string, string>;
}

export async function getLocalBlobUrlFromExternalResource(
    url: string,
    options: IGetLocalBlobUrlFromExternalResourceOptions = {},
) {
    const { onProgressUpdate, headers, timeoutInMilliseconds = 15000 } = options;
    return new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);

        xhr.responseType = "blob";

        if (headers) {
            for (const headerName in headers) {
                xhr.setRequestHeader(headerName, headers[headerName]);
            }
        }

        xhr.onload = function () {
            if (this.status === 200) {
                const blob = new Blob([this.response], { type: this.response.type });
                const blobUrl = URL.createObjectURL(blob);
                resolve(blobUrl);
            } else {
                reject(new APIError(this.response, undefined, url));
            }
        };

        xhr.onerror = function () {
            reject(new Error("Failed to download resource"));
        };

        xhr.ontimeout = function () {
            reject(new Error("Request timed out"));
        };

        xhr.onprogress = onProgressUpdate
            ? function (event) {
                  if (event.lengthComputable) {
                      const percentComplete = (event.loaded / event.total) * 100;
                      onProgressUpdate(percentComplete);
                  }
              }
            : null;

        xhr.timeout = timeoutInMilliseconds;
        xhr.send();
    });
}

interface IUploadFileOptions {
    onProgressUpdate?: (progress: number) => void;
    headers?: Record<string, string>;
}

export async function uploadFile<UploadResponseType = void>(
    file: Blob,
    uploadUrl: string,
    options: IUploadFileOptions = {},
) {
    const { onProgressUpdate, headers } = options;
    return new Promise<UploadResponseType>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", uploadUrl, true);

        xhr.responseType = "json";

        if (headers) {
            for (const headerName in headers) {
                xhr.setRequestHeader(headerName, headers[headerName]);
            }
        }

        xhr.onload = function () {
            if (this.status >= 200 && this.status < 300) {
                resolve(this.response);
            } else {
                reject(new APIError(this.response, undefined, uploadUrl));
            }
        };

        xhr.onerror = function () {
            reject(new Error("Failed to upload file"));
        };

        xhr.ontimeout = function () {
            reject(new Error("Request timed out"));
        };

        xhr.upload.onprogress = onProgressUpdate
            ? function (event) {
                  if (event.lengthComputable && onProgressUpdate) {
                      const percentComplete = (event.loaded / event.total) * 100;
                      onProgressUpdate(percentComplete);
                  }
              }
            : null;
        xhr.send(file);
    });
}

export function getVideoUrl(file: BTFileSystem) {
    const nativeFile = file.nativeFile;
    if (file.isOnNewEntity && nativeFile) {
        return URL.createObjectURL(nativeFile);
    } else {
        return file.docPath;
    }
}

export enum ImageFitMode {
    None,
    Max,
    Pad,
    Crop,
    Carve,
    Stretch,
}

interface IFileImageUrlOptionsBase {
    documentInstanceId: number;
    builderId: number;
    jobId?: number;
    width?: number;
    height?: number;
    imageFitMode?: ImageFitMode;
    /**
     * only use dangerousDownload when we want full uncompressed image download
     */
    dangerousDownload?: boolean;
}

/**
 * @deprecated Use rowVersion instead
 */
interface ILegacyFileImageUrlOptions extends IFileImageUrlOptionsBase {
    /**
     * @deprecated Use rowVersion instead
     * This will lead to bugs and poor performance due to no caching.  Please do not use
     */
    dangerousNoRowVersion: true;
}

interface IFileImageUrlOptions extends IFileImageUrlOptionsBase {
    rowVersion?: string | null;
}

export function getImageUrlFromDocInstanceId(
    options: IFileImageUrlOptions | ILegacyFileImageUrlOptions,
) {
    const queryParams = new URLSearchParams();
    queryParams.append("builderId", options.builderId.toString());

    if (options.jobId) {
        queryParams.append("jobId", options.jobId.toString());
    }

    if (options.width) {
        queryParams.append("width", options.width.toString() + "px");
    }

    if (options.height) {
        queryParams.append("height", options.height.toString() + "px");
    }

    if (options.imageFitMode) {
        queryParams.append("mode", options.imageFitMode.toString());
    }

    if ("rowVersion" in options && options.rowVersion) {
        queryParams.append("rowVersion", options.rowVersion);
    }

    return `/api/files/${options.documentInstanceId}${
        options.dangerousDownload ? "" : "/preview"
    }?${queryParams.toString()}`;
}

interface IFileVideoAndImagesUrlOptions extends IFileImageUrlOptions {
    videoUrl?: string;
    thumbnailUrl?: string;
    showVideoThumbnails?: boolean;
    mediaType: MediaTypes;
    tempMediaUrl?: string;
}

export function getVideoOrImageUrl(options: IFileVideoAndImagesUrlOptions) {
    if (options.tempMediaUrl) {
        return options.tempMediaUrl;
    }
    if (options.mediaType === MediaTypes.Video) {
        return options.showVideoThumbnails ? options.thumbnailUrl : options.videoUrl;
    } else {
        return getImageUrlFromDocInstanceId(options);
    }
}

/**
 * Programmatic file download via a hidden anchor element. Caller-supplied URLs
 * are validated to be same-origin (relative path or matching window.location.origin)
 * as defense-in-depth — the URL is fed straight into anchor.href so an unexpected
 * absolute URL (e.g. from a compromised or misconfigured backend) would otherwise
 * navigate the user off-site. Returns false without clicking when the URL fails
 * the check.
 */
export function triggerAnchorDownload(url: string, fileName?: string): boolean {
    if (!isSameOriginDownloadUrl(url)) {
        return false;
    }
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.rel = "noopener noreferrer";
    if (fileName) {
        anchor.download = fileName;
    }
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return true;
}

function isSameOriginDownloadUrl(url: string): boolean {
    if (!url) {
        return false;
    }
    // Relative URL — implicitly same-origin.
    if (url.startsWith("/") && !url.startsWith("//")) {
        return true;
    }
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.origin === window.location.origin;
    } catch {
        return false;
    }
}
